/**
 * Fitment taxonomy: load, cache, and walk.
 *
 * The whole vehicle tree is one static JSON file, so the cascade runs entirely in
 * memory — no request per dropdown. The shipped export is filtered to the makes
 * that are live on the store (~3.5k vehicles, ~21 KB gzipped); the full 11,102
 * come to ~67 KB. See docs/fitment-api-spec.md §1a and §3.
 *
 * Tier order is Type > Make > Year > Model.
 */
(function () {
  'use strict';

  var CACHE_KEY = 'fmt:taxonomy';
  var TIERS = ['type', 'make', 'year', 'model'];

  var tree = null;
  var meta = null;
  var loading = null;

  /** Must match the Shopify collection handle exactly. */
  function slugify(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null; // private mode, quota, or corrupt entry — just refetch
    }
  }

  function writeCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (e) {
      /* over quota or blocked — the in-memory copy still works for this page */
    }
  }

  /**
   * Resolves once the tree is in memory. Serves the cached copy immediately when
   * present, then revalidates in the background so a taxonomy update lands on the
   * next page view rather than blocking this one.
   */
  function load(url) {
    if (tree) return Promise.resolve(tree);
    if (loading) return loading;
    // Callers with no finder on the page (the brand directory, the family
    // tiles) load without a URL; the layout publishes the asset's URL for them.
    url = url || window.__fitmentTaxonomyUrl;
    if (!url) return Promise.reject(new Error('taxonomy url unknown'));

    var cached = readCache();

    var fetchFresh = fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('taxonomy ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data || !data.tree) throw new Error('taxonomy malformed');
        if (!cached || cached.version !== data.version) writeCache(data);
        return data;
      });

    if (cached && cached.tree) {
      tree = cached.tree;
      meta = { version: cached.version, count: cached.vehicle_count };
      fetchFresh.then(function (data) {
        // Swap in for subsequent interactions; don't yank the tree mid-cascade.
        if (data.version !== cached.version) {
          tree = data.tree;
          index = null;
          meta = { version: data.version, count: data.vehicle_count };
          document.dispatchEvent(new CustomEvent('fitment:taxonomy-updated'));
        }
      })['catch'](function () { /* offline is fine, we have the cache */ });
      return Promise.resolve(tree);
    }

    loading = fetchFresh.then(function (data) {
      tree = data.tree;
      index = null;
      meta = { version: data.version, count: data.vehicle_count };
      loading = null;
      return tree;
    })['catch'](function (err) {
      loading = null;
      throw err;
    });

    return loading;
  }

  /**
   * Options for one tier, given the selections above it.
   * `sel` is { type, make, year, model } with the levels above `tier` filled in.
   */
  function options(tier, sel) {
    if (!tree) return [];
    sel = sel || {};

    if (tier === 'type') return Object.keys(tree);

    var atType = tree[sel.type];
    if (!atType) return [];
    if (tier === 'make') return Object.keys(atType);

    var atMake = atType[sel.make];
    if (!atMake) return [];
    if (tier === 'year') {
      // Descending — the build step can't do this, integer-like object keys
      // always come back ascending.
      return Object.keys(atMake).sort(function (a, b) { return Number(b) - Number(a); });
    }

    var atYear = atMake[sel.year];
    if (!atYear) return [];
    if (tier === 'model') {
      return atYear.map(function (v) { return v.m; });
    }

    return [];
  }

  /** Full selection -> vehicle record, or null if it doesn't resolve. */
  function resolve(sel) {
    if (!tree || !sel || !sel.type || !sel.make || !sel.year || !sel.model) return null;

    var list = ((tree[sel.type] || {})[sel.make] || {})[sel.year];
    if (!list) return null;

    var hit = null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].m === sel.model) { hit = list[i]; break; }
    }
    if (!hit) return null;

    return {
      key: hit.k,
      label: sel.year + ' ' + sel.make + ' ' + hit.m,
      year: Number(sel.year),
      make: sel.make,
      model: hit.m,
      type: sel.type,
      url: '/collections/' + hit.k
    };
  }

  /**
   * Rebuild a full vehicle record from a stored key alone.
   * The garage persists keys, so this is what rehydrates it on load. Linear over
   * ~11k entries, but it only runs on a cache miss and stays under a millisecond.
   */
  function fromKey(key) {
    if (!tree || !key) return null;

    for (var type in tree) {
      for (var make in tree[type]) {
        for (var year in tree[type][make]) {
          var list = tree[type][make][year];
          for (var i = 0; i < list.length; i++) {
            if (list[i].k === key) {
              return {
                key: key,
                label: year + ' ' + make + ' ' + list[i].m,
                year: Number(year),
                make: make,
                model: list[i].m,
                type: type,
                url: '/collections/' + key
              };
            }
          }
        }
      }
    }
    return null;
  }

  var index = null;

  /** Every vehicle as one searchable record; built once, after the tree is in memory. */
  function buildIndex() {
    index = [];
    for (var type in tree) {
      for (var make in tree[type]) {
        for (var year in tree[type][make]) {
          var list = tree[type][make][year];
          for (var i = 0; i < list.length; i++) {
            var label = year + ' ' + make + ' ' + list[i].m;
            var text = label.toLowerCase();
            index.push({
              key: list[i].k, label: label, year: Number(year), make: make, model: list[i].m, type: type,
              url: '/collections/' + list[i].k, text: text, words: text.split(/[^a-z0-9]+/).filter(Boolean)
            });
          }
        }
      }
    }
  }

  /**
   * Machines matching what a customer typed. Every token must be found in
   * the "year make model" text — anywhere in it, so "400r" finds "XR400R"
   * (Shopify's suggestions match whole words only; Lucas, 2026-10-12). A
   * token that starts a word scores higher than one found mid-word; then
   * the newest year first. Returns vehicle records like resolve() gives.
   */
  function search(query, limit) {
    if (!tree) return [];
    if (!index) buildIndex();
    var toks = String(query || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!toks.length) return [];
    var hits = [];
    for (var i = 0; i < index.length; i++) {
      var entry = index[i];
      var score = 0;
      var ok = true;
      for (var t = 0; t < toks.length; t++) {
        var tok = toks[t];
        var starts = false;
        for (var w = 0; w < entry.words.length; w++) {
          if (entry.words[w].indexOf(tok) === 0) { starts = true; break; }
        }
        if (starts) score += 2;
        else if (entry.text.indexOf(tok) !== -1) score += 1;
        else { ok = false; break; }
      }
      if (ok) hits.push({ entry: entry, score: score });
    }
    hits.sort(function (a, b) {
      return (b.score - a.score) || (b.entry.year - a.entry.year) || a.entry.label.localeCompare(b.entry.label);
    });
    return hits.slice(0, limit || 8).map(function (h) {
      var e = h.entry;
      return { key: e.key, label: e.label, year: e.year, make: e.make, model: e.model, type: e.type, url: e.url };
    });
  }

  window.FitmentData = {
    TIERS: TIERS,
    load: load,
    options: options,
    resolve: resolve,
    fromKey: fromKey,
    search: search,
    slugify: slugify,
    get meta() { return meta; },
    get ready() { return tree !== null; }
  };
})();
